// Imported Friends Web Component
import logger from '../utils/logger.js';
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class ImportedFriends extends HTMLElement {
  constructor() {
    super();
    this.friends = [];
    this.filteredFriends = [];
    this.isLoading = false;
    this.errorMessage = null;
    this.currentUser = null;
    this.currentStatus = 'pending';
    this.searchQuery = '';
    this.selectedFriendIds = new Set();
    this.currentPage = 1;
    this.pageSize = 50;
    this.totalFriends = 0;
    this._eventHandlersAttached = false;
  }

  connectedCallback() {
    this._eventHandlersAttached = false;
    this.render();
    this.loadUserAndFriends().then(() => {
      this.attachEventListeners();
    });
  }

  async loadUserAndFriends() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      // Get current user
      const authData = await window.API.request('/auth/status');
      if (!authData.authenticated) {
        throw new Error('User not authenticated');
      }

      this.currentUser = authData.user;
      await this.loadFriends(this.currentStatus);
    } catch (error) {
      logger.error('Failed to load user or friends:', error);
      this.errorMessage = 'Failed to load imported friends';
      this.isLoading = false;
      this.render();
    }
  }

  async loadFriends(status = null, page = 1) {
    this.isLoading = true;
    this.errorMessage = null;
    this.render();

    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const statusParam = status || this.currentStatus;
      const searchParam = this.searchQuery.trim() ? `&search=${encodeURIComponent(this.searchQuery.trim())}` : '';
      const data = await window.API.request(`/imported-friends?status=${statusParam}&page=${page}&limit=${this.pageSize}${searchParam}`);
      this.friends = data.friends || [];
      this.totalFriends = data.total || 0;
      this.currentPage = page;
      this.currentStatus = statusParam;
      this.applyFilters();
    } catch (error) {
      logger.error('Failed to load imported friends:', error);
      this.errorMessage = 'Failed to load imported friends';
      this.friends = [];
      this.totalFriends = 0;
    } finally {
      this.isLoading = false;
      this.render();
      this.attachEventListeners();
    }
  }

  applyFilters() {
    // Search is now handled server-side, so just use the friends array directly
    this.filteredFriends = this.friends;
  }

  handleSearch(e) {
    this.searchQuery = e.target.value;
    // Debounce search to avoid too many API calls
    clearTimeout(this._searchTimeout);
    this._searchTimeout = setTimeout(() => {
      this.currentPage = 1; // Reset to first page on new search
      this.loadFriends(this.currentStatus, 1);
    }, 300);
  }

  handleStatusFilter(e) {
    const status = e.target.value;
    this.currentStatus = status;
    this.selectedFriendIds.clear();
    this.currentPage = 1;
    this.loadFriends(status, 1);
  }

  toggleFriendSelection(friendId) {
    if (this.selectedFriendIds.has(friendId)) {
      this.selectedFriendIds.delete(friendId);
    } else {
      this.selectedFriendIds.add(friendId);
    }
    // Only update the UI without full re-render
    this.updateSelectionUI();
  }

  toggleSelectAll() {
    if (this.selectedFriendIds.size === this.filteredFriends.length) {
      this.selectedFriendIds.clear();
    } else {
      this.selectedFriendIds = new Set(this.filteredFriends.map(f => f.id));
    }
    // Only update the UI without full re-render
    this.updateSelectionUI();
  }

  updateSelectionUI() {
    // Update select all checkbox
    const selectAllCheckbox = this.querySelector('#selectAllCheckbox');
    if (selectAllCheckbox) {
      const allSelected = this.filteredFriends.length > 0 &&
                         this.selectedFriendIds.size === this.filteredFriends.length;
      selectAllCheckbox.checked = allSelected;
    }

    // Update individual checkboxes
    this.querySelectorAll('.friend-checkbox').forEach(checkbox => {
      const friendId = parseInt(checkbox.dataset.friendId);
      checkbox.checked = this.selectedFriendIds.has(friendId);
    });

    // Update friend item selected state
    this.querySelectorAll('.friend-item').forEach(item => {
      const checkbox = item.querySelector('.friend-checkbox');
      if (checkbox) {
        const friendId = parseInt(checkbox.dataset.friendId);
        if (this.selectedFriendIds.has(friendId)) {
          item.classList.add('selected');
        } else {
          item.classList.remove('selected');
        }
      }
    });

    // Update bulk action buttons (only ignore, no bulk invite)
    // Note: Event delegation handles clicks, so we don't need to attach listeners here
    const hasSelected = this.selectedFriendIds.size > 0;
    const bulkActions = this.querySelector('.bulk-actions');
    if (bulkActions) {
      const bulkButtons = bulkActions.querySelector('.bulk-buttons');
      if (hasSelected) {
        if (!bulkButtons) {
          const buttonsDiv = document.createElement('div');
          buttonsDiv.className = 'bulk-buttons';
          buttonsDiv.innerHTML = `
            <button id="bulkIgnoreBtn" class="btn btn-secondary btn-sm">
              Ignore Selected (${this.selectedFriendIds.size})
            </button>
          `;
          bulkActions.appendChild(buttonsDiv);
        } else {
          const btn = bulkButtons.querySelector('#bulkIgnoreBtn');
          if (btn) {
            btn.textContent = `Ignore Selected (${this.selectedFriendIds.size})`;
          }
        }
      } else if (bulkButtons) {
        bulkButtons.remove();
      }
    }
  }

  async inviteFriend(friendId) {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      // Get email from the input field
      const emailInput = this.querySelector(`#email-input-${friendId}`);
      if (!emailInput) {
        alert('Email input not found. Please refresh the page.');
        return;
      }

      const email = emailInput.value.trim();
      if (!email) {
        alert('Please enter an email address before inviting.');
        emailInput.focus();
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert('Please enter a valid email address.');
        emailInput.focus();
        return;
      }

      const body = JSON.stringify({ email });
      await window.API.request(`/imported-friends/${friendId}/invite`, {
        method: 'POST',
        body
      });

      // Optimistically update status instead of full reload
      const friend = this.friends.find(f => f.id === friendId);
      if (friend) {
        friend.status = 'invited';
      }
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to invite friend:', error);
      alert(error.message || 'Failed to invite friend. Please try again.');
      // Reload on error to ensure consistency
      await this.loadFriends(this.currentStatus, this.currentPage);
    }
  }

  async ignoreFriend(friendId) {
    // Add confirmation for single ignore
    if (!confirm('Are you sure you want to ignore this friend?')) {
      return;
    }

    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      await window.API.request(`/imported-friends/${friendId}/ignore`, {
        method: 'POST'
      });

      // Optimistically remove from list instead of full reload
      this.friends = this.friends.filter(f => f.id !== friendId);
      this.filteredFriends = this.filteredFriends.filter(f => f.id !== friendId);
      this.selectedFriendIds.delete(friendId);
      this.totalFriends = Math.max(0, this.totalFriends - 1);
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to ignore friend:', error);
      alert('Failed to ignore friend. Please try again.');
      // Reload on error to ensure consistency
      await this.loadFriends(this.currentStatus, this.currentPage);
    }
  }


  async bulkIgnore() {
    if (this.selectedFriendIds.size === 0) {
      alert('Please select at least one friend to ignore.');
      return;
    }

    if (!confirm(`Ignore ${this.selectedFriendIds.size} friend(s)?`)) {
      return;
    }

    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      await window.API.request('/imported-friends/bulk-ignore', {
        method: 'POST',
        body: JSON.stringify({
          friendIds: Array.from(this.selectedFriendIds)
        })
      });

      // Optimistically remove from list instead of full reload
      const ignoredCount = this.selectedFriendIds.size;
      this.friends = this.friends.filter(f => !this.selectedFriendIds.has(f.id));
      this.filteredFriends = this.filteredFriends.filter(f => !this.selectedFriendIds.has(f.id));
      this.selectedFriendIds.clear();
      this.totalFriends = Math.max(0, this.totalFriends - ignoredCount);
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to bulk ignore friends:', error);
      alert('Failed to ignore friends. Please try again.');
      // Reload on error to ensure consistency
      await this.loadFriends(this.currentStatus, this.currentPage);
    }
  }

  getStatusBadge(status) {
    const badges = {
      pending: '<span class="status-badge status-pending">Pending</span>',
      invited: '<span class="status-badge status-invited">Invited</span>',
      ignored: '<span class="status-badge status-ignored">Ignored</span>',
      accepted: '<span class="status-badge status-accepted">Accepted</span>'
    };
    return badges[status] || `<span class="status-badge">${status}</span>`;
  }

  attachEventListeners() {
    // Only attach event handlers once
    if (this._eventHandlersAttached) {
      return;
    }

    // Status filter
    const statusFilter = this.querySelector('#statusFilter');
    if (statusFilter) {
      statusFilter.addEventListener('change', (e) => {
        this.currentPage = 1; // Reset to first page on status change
        this.handleStatusFilter(e);
      });
    }

    // Search input
    const searchInput = this.querySelector('#searchInput');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => this.handleSearch(e));
    }

    // Select all checkbox
    const selectAllCheckbox = this.querySelector('#selectAllCheckbox');
    if (selectAllCheckbox) {
      selectAllCheckbox.addEventListener('change', () => this.toggleSelectAll());
    }

    // Use event delegation for dynamic elements (only attach once)
    this.addEventListener('change', (e) => {
      if (e.target.classList.contains('friend-checkbox')) {
        const friendId = parseInt(e.target.dataset.friendId);
        this.toggleFriendSelection(friendId);
      }
    });

    this.addEventListener('click', async (e) => {
      // Prevent double-clicks
      if (e.target.dataset.processing === 'true') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Invite buttons
      if (e.target.classList.contains('invite-btn')) {
        e.preventDefault();
        e.stopPropagation();
        e.target.dataset.processing = 'true';
        const friendId = parseInt(e.target.dataset.friendId);
        await this.inviteFriend(friendId);
        e.target.dataset.processing = 'false';
      }
      // Ignore buttons
      else if (e.target.classList.contains('ignore-btn')) {
        e.preventDefault();
        e.stopPropagation();
        e.target.dataset.processing = 'true';
        const friendId = parseInt(e.target.dataset.friendId);
        await this.ignoreFriend(friendId);
        e.target.dataset.processing = 'false';
      }
      // Bulk actions
      else if (e.target.id === 'bulkIgnoreBtn') {
        e.preventDefault();
        e.stopPropagation();
        e.target.dataset.processing = 'true';
        await this.bulkIgnore();
        e.target.dataset.processing = 'false';
      }
      // Pagination buttons
      else if (e.target.classList.contains('page-btn')) {
        e.preventDefault();
        e.stopPropagation();
        const page = parseInt(e.target.dataset.page);
        if (page && page !== this.currentPage) {
          this.loadFriends(this.currentStatus, page);
        }
      }
      // Return to settings button
      else if (e.target.id === 'returnToSettingsBtn') {
        e.preventDefault();
        e.stopPropagation();
        window.router.navigate('/settings');
      }
    });

    // Handle Enter key in email inputs
    this.addEventListener('keydown', async (e) => {
      if (e.target.classList.contains('email-input') && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const friendId = parseInt(e.target.id.replace('email-input-', ''));
        if (friendId) {
          await this.inviteFriend(friendId);
        }
      }
    });

    this._eventHandlersAttached = true;
  }

  render() {
    if (this.isLoading && this.friends.length === 0) {
      this.innerHTML = `
        <div class="imported-friends">
          <div class="loading-state">
            <div class="spinner"></div>
            <div>Loading imported friends...</div>
          </div>
        </div>
      `;
      return;
    }

    const hasSelected = this.selectedFriendIds.size > 0;
    const allSelected = this.filteredFriends.length > 0 &&
                       this.selectedFriendIds.size === this.filteredFriends.length;

    this.innerHTML = `
      <div class="imported-friends">
        <div class="settings-header">
          ${renderPageTitle('👥', 'Imported Friends')}
          <p>Manage friends imported from your Facebook export</p>
        </div>

        ${this.errorMessage ? `
          <div class="error-message">
            ${escapeHtml(this.errorMessage)}
          </div>
        ` : ''}

        <div class="filters-section">
          <div class="filter-group">
            <label for="statusFilter">Filter by Status:</label>
            <select id="statusFilter">
              <option value="pending" ${this.currentStatus === 'pending' ? 'selected' : ''}>Pending</option>
              <option value="invited" ${this.currentStatus === 'invited' ? 'selected' : ''}>Invited</option>
              <option value="ignored" ${this.currentStatus === 'ignored' ? 'selected' : ''}>Ignored</option>
              <option value="accepted" ${this.currentStatus === 'accepted' ? 'selected' : ''}>Accepted</option>
            </select>
          </div>

          <div class="filter-group">
            <label for="searchInput">Search:</label>
            <input 
              type="text" 
              id="searchInput" 
              placeholder="Search by name or email..."
              value="${escapeHtml(this.searchQuery)}"
            />
          </div>
        </div>

        ${this.filteredFriends.length > 0 ? `
          <div class="bulk-actions">
            <label class="checkbox-label">
              <input 
                type="checkbox" 
                id="selectAllCheckbox"
                ${allSelected ? 'checked' : ''}
              />
              <span>Select All (${this.filteredFriends.length})</span>
            </label>
            ${hasSelected ? `
              <div class="bulk-buttons">
                <button id="bulkIgnoreBtn" class="btn btn-secondary btn-sm">
                  Ignore Selected (${this.selectedFriendIds.size})
                </button>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <div class="friends-section">
          ${this.filteredFriends.length === 0 ? `
            <div class="empty-state">
              <p>${this.friends.length === 0
    ? 'No imported friends found. Import your Facebook friends first!'
    : 'No friends match your search criteria.'}</p>
            </div>
          ` : `
            <div class="friends-list">
              ${this.filteredFriends.map(friend => {
    const isSelected = this.selectedFriendIds.has(friend.id);
    const canInvite = friend.status === 'pending';
    const canIgnore = friend.status === 'pending' || friend.status === 'invited';

    return `
                  <div class="friend-item ${isSelected ? 'selected' : ''}">
                    <div class="friend-checkbox-container">
                      <input 
                        type="checkbox" 
                        class="friend-checkbox"
                        data-friend-id="${friend.id}"
                        ${isSelected ? 'checked' : ''}
                      />
                    </div>
                    <div class="friend-info">
                      <div class="friend-name">
                        ${escapeHtml(friend.facebookName || 'Unknown')}
                        ${this.getStatusBadge(friend.status)}
                      </div>
                    </div>
                    <div class="friend-email-input">
                      <input 
                        type="email" 
                        id="email-input-${friend.id}"
                        class="email-input"
                        placeholder="Enter email address"
                        value="${friend.email ? escapeHtml(friend.email) : ''}"
                        ${friend.status === 'invited' || friend.status === 'accepted' ? 'disabled' : ''}
                      />
                    </div>
                    <div class="friend-actions">
                      ${canInvite ? `
                        <button 
                          class="btn btn-primary btn-sm invite-btn"
                          data-friend-id="${friend.id}"
                        >
                          Invite
                        </button>
                      ` : ''}
                      ${canIgnore ? `
                        <button 
                          class="btn btn-secondary btn-sm ignore-btn"
                          data-friend-id="${friend.id}"
                        >
                          Ignore
                        </button>
                      ` : ''}
                    </div>
                  </div>
                `;
  }).join('')}
            </div>
          `}
        </div>

        ${this.totalFriends > this.pageSize ? `
          <div class="pagination">
            <div class="pagination-info">
              Showing ${((this.currentPage - 1) * this.pageSize) + 1} - ${Math.min(this.currentPage * this.pageSize, this.totalFriends)} of ${this.totalFriends}
            </div>
            <div class="pagination-buttons">
              ${this.currentPage > 1 ? `
                <button class="btn btn-secondary btn-sm page-btn" data-page="${this.currentPage - 1}">
                  ← Previous
                </button>
              ` : ''}
              ${this.currentPage * this.pageSize < this.totalFriends ? `
                <button class="btn btn-secondary btn-sm page-btn" data-page="${this.currentPage + 1}">
                  Next →
                </button>
              ` : ''}
            </div>
          </div>
        ` : ''}

        <div class="settings-footer">
          <button class="btn btn-secondary" id="returnToSettingsBtn">
            ← Return to Settings
          </button>
        </div>
      </div>
    `;

    // Re-attach listeners after render
    this.attachEventListeners();
  }
}

// Register the custom element
customElements.define('imported-friends', ImportedFriends);

